import { restClient } from '@polygon.io/client-js';

const POLYGON_API_KEY = process.env.REACT_APP_POLYGON_API_KEY;

// Initialize the Polygon.io REST client
const polygonClient = restClient(POLYGON_API_KEY);

// Helper function to make API calls with proper error handling
const makeApiCall = async (apiCall) => {
  try {
    const data = await apiCall;
    if (data.error) {
      throw new Error(data.error || 'API returned an error');
    }
    return data;
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
};

export const fetchStockData = async (symbol) => {
  try {
    console.log(`Fetching stock data for ${symbol} directly from Polygon.io`);
    
    // Call Polygon API directly using the client
    const previousClose = await makeApiCall(polygonClient.stocks.previousClose(symbol));
    console.log(`Received previous close data for ${symbol}:`, previousClose);
    
    // Ensure we have results
    if (!previousClose.results || previousClose.results.length === 0) {
      throw new Error(`No data returned for ${symbol}`);
    }
    
    // Get ticker details to get name and market cap
    const ticker = await makeApiCall(polygonClient.reference.tickerDetails(symbol));
    console.log(`Received ticker details for ${symbol}:`, ticker);
    
    // Extract the first result (most recent data point)
    const stockData = previousClose.results[0];
    
    // Enhanced market cap retrieval with multiple fallback methods
    let marketCap = null;
    
    // Try main location first
    if (ticker.results?.market_cap) {
      marketCap = ticker.results.market_cap;
    } 
    // Try alternative property names
    else if (ticker.results?.marketcap) {
      marketCap = ticker.results.marketcap;
    }
    // Try stats object
    else if (ticker.results?.stats?.market_cap) {
      marketCap = ticker.results.stats.market_cap;
    } 
    // If still null, try to get it from snapshot endpoint
    else {
      try {
        console.log(`Trying snapshot endpoint for ${symbol} market cap`);
        const snapshot = await makeApiCall(polygonClient.stocks.snapshotAllTickers({
          tickers: symbol
        }));
        console.log(`Received snapshot data for ${symbol}:`, snapshot);
        
        if (snapshot.tickers && snapshot.tickers.length > 0) {
          if (snapshot.tickers[0].market_cap) {
            marketCap = snapshot.tickers[0].market_cap;
          } else if (snapshot.tickers[0].day?.market_cap) {
            marketCap = snapshot.tickers[0].day.market_cap;
          }
        }
      } catch (snapshotError) {
        console.error(`Error fetching snapshot data for ${symbol}:`, snapshotError);
        // Continue with null market cap
      }
    }
    
    // If still null and we have shares outstanding data, calculate manually
    if (marketCap === null) {
      let sharesOutstanding = null;
      
      // Check for shares outstanding in various possible locations
      if (ticker.results?.weighted_shares_outstanding) {
        sharesOutstanding = ticker.results.weighted_shares_outstanding;
      } else if (ticker.results?.share_class_shares_outstanding) {
        sharesOutstanding = ticker.results.share_class_shares_outstanding;
      } else if (ticker.results?.shares_outstanding) {
        sharesOutstanding = ticker.results.shares_outstanding;
      }
      
      // If we have shares outstanding, calculate market cap
      if (sharesOutstanding && stockData.c) {
        marketCap = sharesOutstanding * stockData.c;
        console.log(`Calculated market cap for ${symbol}: ${sharesOutstanding} shares × $${stockData.c} = $${marketCap}`);
      }
    }
    
    // Convert market cap to billions if available
    const marketCapB = marketCap !== null 
      ? (marketCap / 1000000000).toFixed(2)
      : null;
    
    // Format the data for our frontend
    const data = {
      symbol: stockData.T,
      name: ticker.results?.name || symbol,
      price: stockData.c,
      open: stockData.o,
      high: stockData.h,
      low: stockData.l,
      volume: stockData.v,
      timestamp: stockData.t,
      change: stockData.c - stockData.o,
      changePercent: ((stockData.c - stockData.o) / stockData.o) * 100,
      marketCapB: marketCapB,
      volatility: calculateVolatilityFromPreviousClose(previousClose.results),
    };
    
    console.log(`Successfully formatted data for ${symbol}:`, data);
    return data;
  } catch (error) {
    console.error('API Error Details:', {
      symbol,
      message: error.message,
      status: error.status,
      response: error.response,
      stack: error.stack
    });
    throw error;
  }
};

// Calculate volatility from previous close data
const calculateVolatilityFromPreviousClose = (data) => {
  if (!data || data.length === 0) {
    return null;
  }
  
  // Simple volatility based on high-low range relative to closing price
  const range = data[0].h - data[0].l;
  const close = data[0].c;
  
  if (close === 0) return null;
  return (range / close).toFixed(4);
}; 